import { useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ArrowRight, Maximize2, DoorOpen } from "lucide-react";
import { formatPrezzo } from "@/data/properties";
import { fileUrl } from "@/lib/api";

// Coordinate indicative attorno a Corato (BA), derivate in modo deterministico
const BASE = { lat: 41.1536, lng: 16.4111 };
const toLatLng = (p) => {
  const x = p.coords?.x ?? 50;
  const y = p.coords?.y ?? 50;
  return [BASE.lat + (0.5 - y / 100) * 0.045, BASE.lng + (x / 100 - 0.5) * 0.06];
};

const pinIcon = (price) =>
  L.divIcon({
    className: "casamore-pin",
    html: `<div style="transform:translate(-50%,-100%);display:flex;flex-direction:column;align-items:center">
      <span style="background:#0F5C4A;color:#fff;font-family:Outfit,sans-serif;font-weight:700;font-size:12px;padding:5px 10px;border-radius:999px;white-space:nowrap;box-shadow:0 6px 18px rgba(15,92,74,.35)">${price}</span>
      <span style="width:2px;height:10px;background:#0F5C4A"></span>
      <span style="width:10px;height:10px;background:#0F5C4A;border-radius:50%;margin-top:-3px;border:2px solid #fff"></span>
    </div>`,
    iconSize: [0, 0],
  });

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 0) return;
    if (points.length === 1) { map.setView(points[0], 15); return; }
    map.fitBounds(points, { padding: [60, 60] });
  }, [points, map]);
  return null;
}

export default function MapView({ properties }) {
  const withPos = useMemo(() => properties.map((p) => ({ ...p, pos: toLatLng(p) })), [properties]);
  const points = useMemo(() => withPos.map((p) => p.pos), [withPos]);

  return (
    <div className="rounded-3xl overflow-hidden border border-border-subtle" data-testid="map-view">
      <MapContainer
        center={[BASE.lat, BASE.lng]}
        zoom={14}
        scrollWheelZoom={false}
        style={{ height: "560px", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
        />
        <FitBounds points={points} />
        {withPos.map((p) => (
          <Marker key={p.id} position={p.pos} icon={pinIcon(formatPrezzo(p))} data-testid={`map-pin-${p.id}`}>
            <Popup>
              <div style={{ width: 220 }} data-testid={`map-popup-${p.id}`}>
                <img src={fileUrl(p.immagini?.[0])} alt={p.titolo} style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 10 }} />
                <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 22, fontWeight: 600, color: "#0F5C4A", marginTop: 8 }}>{formatPrezzo(p)}</div>
                <div style={{ fontWeight: 600, color: "#1A1C1A", fontSize: 14 }}>{p.titolo}</div>
                <div style={{ color: "#6b7280", fontSize: 12 }}>{p.comune} — {p.zona}</div>
                <div style={{ display: "flex", gap: 12, color: "#4b5563", fontSize: 12, marginTop: 4 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Maximize2 size={12} /> {p.superficie} mq</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><DoorOpen size={12} /> {p.locali} locali</span>
                </div>
                <Link
                  to={`/immobili/${p.id}`}
                  data-testid={`map-detail-link-${p.id}`}
                  style={{ marginTop: 8, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%", background: "#0F5C4A", color: "#fff", fontWeight: 600, fontSize: 13, padding: "9px 0", borderRadius: 999, textDecoration: "none" }}
                >
                  Scopri l'immobile <ArrowRight size={14} />
                </Link>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
